import { Boards, type GetBoardResponse, type ListBoardsParams } from '@air/api';
import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { PageLimits } from '~/constants/PageLimits';
import { getPageBoardKey } from '~/constants/react-query-keys';

export const PRIVATE_BOARDS_LIST_KEY = 'PRIVATE_BOARDS_LIST';

export const getPrivateBoardsListKey = (params: UsePrivateBoardsListParams) => {
  return [PRIVATE_BOARDS_LIST_KEY, params];
};

export const isPrivateBoardsListKey = (key: unknown): key is UsePrivateBoardsListParams => {
  return Array.isArray(key) && key[0] === PRIVATE_BOARDS_LIST_KEY && isObject(key[1]);
};

export type UsePrivateBoardsListParams = Omit<ListBoardsParams, 'cursor' | 'workspaceId'> & {
  workspaceId: string | undefined;
};

export type UsePrivateBoardsListOptions = { enabled?: boolean };

export const usePrivateBoardsList = (params: UsePrivateBoardsListParams, options?: UsePrivateBoardsListOptions) => {
  const queryClient = useQueryClient();
  const { workspaceId } = params;

  return useInfiniteQuery({
    queryKey: getPrivateBoardsListKey(params),
    queryFn: !workspaceId
      ? skipToken
      : async ({ pageParam }) => {
          const res = await Boards.list({
            cursor: pageParam || null,
            includeAncestors: true,
            limit: params.limit
              ? params.limit
              : pageParam || null
              ? PageLimits.subBoards.more
              : PageLimits.subBoards.init,
            numThumbnails: params.numThumbnails ?? 1,
            ...params,
            workspaceId,
          });

          res.data.forEach((board) => {
            const cachedBoard = queryClient
              .getQueryCache()
              .get<GetBoardResponse>(JSON.stringify(getPageBoardKey({ boardId: board.id })))?.state.data;

            if (!cachedBoard) {
              queryClient.setQueryData<GetBoardResponse>(getPageBoardKey({ boardId: board.id }), board);
            }
          });

          return res;
        },
    enabled: !!workspaceId && options?.enabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
    ...options,
  });
};
